import React, { useState, useEffect } from 'react';
import { Plus, Save, X, AlertCircle, FileText } from 'lucide-react';
import apiService from '../../../services/api';
import { PromptTemplate, PromptTemplateVersion } from '../../../types/prompt';
import { extractVariables, validateTemplate, getTemplateTypeColor } from '../../../utils/prompt-helpers';
import TemplateVersionHistory from './TemplateVersionHistory';
import TemplateVariableList from './TemplateVariableList';

interface NotificationState {
  type: 'success' | 'error' | null;
  message: string;
}

const templateTypes = [
  { value: 'system', label: 'System Prompt', description: 'Main instructions for AI' },
  { value: 'user', label: 'User Prompt', description: 'User message template' },
  { value: 'analysis', label: 'Analysis Framework', description: 'Analysis methodology' },
  { value: 'scoring', label: 'Scoring Guidelines', description: 'Risk scoring rules' },
];

export default function PromptTemplateEditor() {
  const [templates, setTemplates] = useState<PromptTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<PromptTemplate | null>(null);
  const [selectedVersion, setSelectedVersion] = useState<PromptTemplateVersion | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState<NotificationState>({ type: null, message: '' });
  
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    templateType: 'system',
    template: '',
    variables: [] as string[],
    modelRestrictions: [] as string[],
    createNewVersion: false,
    versionNotes: '',
  });

  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  useEffect(() => {
    fetchTemplates();
  }, []);

  useEffect(() => {
    if (notification.type) {
      const timer = setTimeout(() => {
        setNotification({ type: null, message: '' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/api/admin/prompts/templates?includeVersions=true');
      setTemplates(response.data);
    } catch (error) {
      setNotification({ type: 'error', message: 'Failed to load templates' });
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateChange = (value: string) => {
    setFormData({ ...formData, template: value });
    
    // Extract and validate variables
    const variables = extractVariables(value);
    setFormData(prev => ({ ...prev, variables }));
    
    // Validate template
    const validation = validateTemplate(value);
    setValidationErrors(validation.errors);
  };

  const handleSelectTemplate = async (template: PromptTemplate) => {
    setSelectedTemplate(template);
    setIsCreating(false);
    
    // Load full template details
    try {
      const response = await apiService.get(`/api/admin/prompts/templates/${template.id}`);
      const fullTemplate = response.data;
      setSelectedTemplate(fullTemplate);
      
      // Update form data with template details
      setFormData({
        ...formData,
        code: fullTemplate.code,
        name: fullTemplate.name,
        description: fullTemplate.description || '',
        templateType: fullTemplate.templateType,
        modelRestrictions: fullTemplate.modelRestrictions || [],
      });
      
      // Select the active version or the latest
      const activeVersion = fullTemplate.promptVersions?.find((v: PromptTemplateVersion) => v.isActive) 
        || fullTemplate.promptVersions?.[0];
      
      if (activeVersion) {
        handleSelectVersion(activeVersion);
      }
    } catch (error) {
      setNotification({ type: 'error', message: 'Failed to load template details' });
    }
  };

  const handleSelectVersion = (version: PromptTemplateVersion) => {
    setSelectedVersion(version);
    setFormData({
      ...formData,
      template: version.template,
      variables: version.variables,
    });
  };

  const handleSave = async () => {
    if (validationErrors.length > 0) {
      setNotification({ type: 'error', message: 'Please fix template errors before saving' });
      return;
    }

    try {
      if (isCreating) {
        await apiService.post('/api/admin/prompts/templates', {
          code: formData.code,
          name: formData.name,
          description: formData.description,
          templateType: formData.templateType,
          template: formData.template,
          variables: formData.variables,
          modelRestrictions: formData.modelRestrictions,
        });
        setNotification({ type: 'success', message: 'Template created successfully' });
      } else if (selectedTemplate) {
        await apiService.put(`/api/admin/prompts/templates/${selectedTemplate.id}`, {
          name: formData.name,
          description: formData.description,
          template: formData.template,
          variables: formData.variables,
          createNewVersion: formData.createNewVersion,
          versionNotes: formData.versionNotes,
        });
        setNotification({ type: 'success', message: 'Template updated successfully' });
      }
      
      fetchTemplates();
      resetForm();
    } catch (error) {
      setNotification({ type: 'error', message: 'Failed to save template' });
    }
  };

  const resetForm = () => {
    setIsCreating(false);
    setIsEditing(false);
    setFormData({
      code: '',
      name: '',
      description: '',
      templateType: 'system',
      template: '',
      variables: [],
      modelRestrictions: [],
      createNewVersion: false,
      versionNotes: '',
    });
    setValidationErrors([]);
  };

  const handleCopyVariable = (variable: string) => {
    navigator.clipboard.writeText(variable);
    setNotification({ type: 'success', message: 'Variable copied to clipboard' });
  };

  return (
    <div className="p-6">
      {/* Help Section */}
      <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
        <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">What are Prompt Templates?</h3>
        <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
          Prompt Templates are reusable text templates that get filled with dynamic data during analysis:
        </p>
        <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1 ml-4">
          <li>
            • <strong>System Prompts</strong>: Main instructions telling AI how to behave
            <div className="text-xs text-gray-600 dark:text-gray-400 ml-4 mt-1">Used as the system message in API calls. Sets the AI's role and behavior.</div>
          </li>
          <li>
            • <strong>User Prompts</strong>: The actual analysis request with article content
            <div className="text-xs text-gray-600 dark:text-gray-400 ml-4 mt-1">Contains the article and specific analysis instructions.</div>
          </li>
          <li>
            • <strong>Analysis Templates</strong>: Structured analysis methodology instructions
            <div className="text-xs text-gray-600 dark:text-gray-400 ml-4 mt-1">Defines HOW to analyze threats (patterns, indicators, methodology).</div>
          </li>
          <li>
            • <strong>Scoring Guidelines</strong>: How to score threats on 0-10 scale
            <div className="text-xs text-gray-600 dark:text-gray-400 ml-4 mt-1">Specific criteria for assigning risk scores to each category.</div>
          </li>
        </ul>
        <p className="text-sm text-gray-900 dark:text-gray-100 mt-3 font-medium">
          Templates support variables like {'{{countryName}}'}, {'{{threatCategories}}'}, etc. that get replaced with actual values.
        </p>
      </div>

      {/* Notification */}
      {notification.type && (
        <div className={`mb-4 p-4 rounded-lg ${
          notification.type === 'success' 
            ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' 
            : 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
        }`}>
          {notification.message}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Template List */}
        <div className="lg:col-span-1">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Templates
            </h3>
            <button
              onClick={() => {
                setIsCreating(true);
                setSelectedTemplate(null);
                resetForm();
              }}
              className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
            >
              <Plus className="w-4 h-4" />
              New
            </button>
          </div>

          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading templates...</div>
          ) : (
            <div className="space-y-2">
              {templates.map((template) => (
                <button
                  key={template.id}
                  onClick={() => handleSelectTemplate(template)}
                  className={`
                    w-full text-left p-3 rounded-lg border transition-colors
                    ${selectedTemplate?.id === template.id
                      ? 'border-blue-500 bg-blue-100'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }
                  `}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">
                        {template.name}
                      </h4>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`px-2 py-0.5 text-xs rounded ${getTemplateTypeColor(template.templateType)}`}>
                          {template.templateType}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {template.code}
                        </span>
                      </div>
                    </div>
                    {template.isActive && (
                      <FileText className="w-4 h-4 text-gray-400" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Template Editor */}
        <div className="lg:col-span-2">
          {(selectedTemplate || isCreating) ? (
            <div className="space-y-6">
              {/* Editor Header */}
              <div className="flex justify-between items-start">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {isCreating ? 'Create Template' : 'Edit Template'}
                </h3>
                <button
                  onClick={resetForm}
                  className="p-1 text-gray-500 hover:text-gray-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Form */}
              <div className="space-y-4">
                {isCreating && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Code (unique identifier)
                      </label>
                      <input
                        type="text"
                        value={formData.code}
                        onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        placeholder="e.g., tier2_analysis"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Type
                      </label>
                      <select
                        value={formData.templateType}
                        onChange={(e) => setFormData({ ...formData, templateType: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      >
                        {templateTypes.map(type => (
                          <option key={type.value} value={type.value}>
                            {type.label} - {type.description}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Template Content
                  </label>
                  <textarea
                    value={formData.template}
                    onChange={(e) => handleTemplateChange(e.target.value)}
                    rows={10}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white font-mono text-sm"
                    placeholder="Enter your template with {{variables}}..."
                  />
                  
                  {validationErrors.length > 0 && (
                    <div className="mt-2 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 mt-0.5" />
                        <div className="text-sm text-red-700 dark:text-red-300">
                          {validationErrors.map((error, i) => (
                            <p key={i}>{error}</p>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {!isCreating && (
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="createVersion"
                      checked={formData.createNewVersion}
                      onChange={(e) => setFormData({ ...formData, createNewVersion: e.target.checked })}
                      className="rounded"
                    />
                    <label htmlFor="createVersion" className="text-sm text-gray-700 dark:text-gray-300">
                      Create new version
                    </label>
                  </div>
                )}

                {formData.createNewVersion && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Version Notes
                    </label>
                    <input
                      type="text"
                      value={formData.versionNotes}
                      onChange={(e) => setFormData({ ...formData, versionNotes: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      placeholder="What changed in this version?"
                    />
                  </div>
                )}

                <div className="flex justify-end gap-2">
                  <button
                    onClick={resetForm}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={validationErrors.length > 0}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Save className="w-4 h-4" />
                    Save
                  </button>
                </div>
              </div>

              {/* Side panels */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TemplateVariableList
                  variables={formData.variables}
                  onCopyVariable={handleCopyVariable}
                />
                
                {selectedTemplate?.promptVersions && (
                  <TemplateVersionHistory
                    versions={selectedTemplate.promptVersions}
                    onSelectVersion={handleSelectVersion}
                    currentVersionId={selectedVersion?.id}
                  />
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              Select a template to edit or create a new one
            </div>
          )}
        </div>
      </div>
    </div>
  );
}